export function toTitleCase(input: string): string {
  if (!input) return "";

  // Normalize separators to spaces and collapse multiple spaces
  const normalized = input.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();

  const smallWords = new Set([
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "for",
    "from",
    "in",
    "nor",
    "of",
    "on",
    "or",
    "per",
    "the",
    "to",
    "via",
    "vs",
    "with",
  ]);

  const acronyms = new Set(["AI"]);

  const words = normalized.split(" ");
  return words
    .map((raw, idx) => {
      const cleanUpper = raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

      // Preserve known acronyms (e.g., AI)
      if (acronyms.has(cleanUpper)) return cleanUpper;

      // Lowercase small words except if first or last
      const isEdge = idx === 0 || idx === words.length - 1;
      if (!isEdge && smallWords.has(raw.toLowerCase())) {
        return raw.toLowerCase();
      }

      // Default Title Case for the word
      return raw
        ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
        : "";
    })
    .join(" ");
}

export function snakeCaseToTitleCase(input: string): string {
  if (!input) {
    return "";
  }

  // Handle single words (no underscores)
  if (!input.includes("_")) {
    return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
  }

  const words = input.split("_");
  const titleCaseWords = words.map(
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  return titleCaseWords.join(" ");
}

// Alias utility matching requested naming; delegates to snakeCaseToTitleCase
export function underscoreToTitleCase(input: string): string {
  return snakeCaseToTitleCase(input);
}

export enum MimeTypes {
  IMAGE_JPEG = "image/jpeg",
  IMAGE_PNG = "image/png",
  IMAGE_GIF = "image/gif",
  IMAGE_WEBP = "image/webp",
  VIDEO_MP4 = "video/mp4",
  APPLICATION_PDF = "application/pdf",
}

const mimeTypeMap = {
  "image/jpeg": "jpeg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "video/mp4": "mp4",
  "application/pdf": "pdf",
};

const extensionToTypeHint = {
  jpeg: "public.jpeg",
  png: "public.png",
  gif: "public.gif",
  webp: "public.webp",
  mp4: "public.mp4",
  pdf: "com.adobe.pdf",
};

export function getTypeHintFromUri(uri: string): string {
  const extension = getFileExtension(uri, getFileTypeFromUri(uri));
  if (!extension) {
    return "";
  }

  return extensionToTypeHint[extension as keyof typeof extensionToTypeHint];
}

export function getFileTypeFromUri(uri: string): MimeTypes {
  if (!uri) {
    return MimeTypes.IMAGE_JPEG;
  }

  const fileType = uri.toLowerCase().endsWith(".png")
    ? "image/png"
    : uri.toLowerCase().endsWith(".gif")
    ? "image/gif"
    : uri.toLowerCase().endsWith(".webp")
    ? "image/webp"
    : uri.toLowerCase().endsWith(".mp4")
    ? "video/mp4"
    : uri.toLowerCase().endsWith(".pdf")
    ? "application/pdf"
    : "image/jpeg";

  return fileType as MimeTypes;
}

export function getFileExtension(uri: string, mimeType: MimeTypes): string {
  const uriMatch = uri.match(/\.([^.]+)$/);
  if (uriMatch) {
    return `.${uriMatch[1].toLowerCase()}`;
  }

  return mimeTypeMap[mimeType as keyof typeof mimeTypeMap] || "";
}

export function setFileExtensionToLowercase(uri: string): string {
  const uriMatch = uri.match(/\.([^.]+)$/);
  if (!uriMatch) return uri;

  const extension = `.${uriMatch[1]}`;
  return uri.replace(extension, extension.toLowerCase());
}

export function extractUUID(str: string): string | null {
  // UUID regex pattern (8-4-4-4-12 format)

  if (!str) return null;
  const uuidRegex =
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

  const match = str.match(uuidRegex);
  return match ? match[0] : null;
}

export function camelCaseToTitleCase(str: string): string {
  if (!str) return "";
  return str.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
    return str.toUpperCase();
  });
}

export function isDependentAccount(source: string, dbUserId: string) {
  const uuid = extractUUID(source || "");
  if (!uuid) {
    return false;
  }

  return uuid === dbUserId;
}
